import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  VoiceAsteriskDowngradeEditForm,
  VoiceAsteriskDowngradeEditInitialValues,
  VoiceAsteriskDowngradeComponents,
  VoiceAsteriskDowngradeAdditionalParams,
  VoiceAsteriskDowngradeFormOptions,
  onVoiceAsteriskDowngradeFormSaved,
  IVoiceAsteriskDowngradeFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedVoiceAsteriskDowngrade, ISharedPatchVoiceAsteriskDowngrade, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchVoiceAsteriskDowngrade, patchVoiceAsteriskDowngrade, getVoiceAsteriskDowngradeUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedVoiceAsteriskDowngrade> {}

const VoiceAsteriskDowngradeFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchVoiceAsteriskDowngrade(id).then(voice_asterisk_downgrade => setValue(voice_asterisk_downgrade));
  }, [id, setValue, ]);

  const param_props: IVoiceAsteriskDowngradeFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && VoiceAsteriskDowngradeEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && VoiceAsteriskDowngradeFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchVoiceAsteriskDowngrade, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchVoiceAsteriskDowngrade) => value && save(() => patchVoiceAsteriskDowngrade(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onVoiceAsteriskDowngradeFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(null, CrudEnum.Update, PermissionEnum.VoiceAsterisk, value)}
    >
      <AtlasForm<ISharedPatchVoiceAsteriskDowngrade>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={VoiceAsteriskDowngradeComponents}
        additionalParams={VoiceAsteriskDowngradeAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={VoiceAsteriskDowngradeEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('VOICE_ASTERISK_DOWNGRADE')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const VoiceAsteriskDowngradeFormEditView = React.memo(withEdit(VoiceAsteriskDowngradeFormEdit, getVoiceAsteriskDowngradeUpdateSchema));
